// ============================================================================
// view.weighinsessions — the shared four-species weigh-in record page
// ============================================================================
// /weigh-in-sessions/<id> is the one authenticated surface with no VIEW_TO_PATH
// entry: it is URL-reachable only, and it renders cattle, sheep, pig and
// broiler sessions from one component. Its ten physical reads all lived in a
// single `loadAll` on the page. Three catalog resources own them now, and the
// split follows the dependency chain loadAll already had rather than being
// imposed on it:
//
//   weighin.session.record      the session row. It decides the species, and
//                               the species decides whether anything else may
//                               be read at all, so it cannot share a resource
//                               with the reads that follow it.
//   weighin.session.detail      the entry list, the cattle/sheep animal
//                               directory, the species session census, the
//                               species weigh-in history, and the two keyed
//                               app_store blobs (ppp-feeders-v1 for pig,
//                               ppp-v4 for broiler). Its `species` param is
//                               what makes those branches reachable, and what
//                               stops a cattle payload painting into a pig
//                               mount.
//   weighin.session.pigMetrics  the pig_session_metrics RPC and the prior
//                               session it names.
//
// WHY THE PIG METRICS ARE THEIR OWN RESOURCE.
//
// Because their REFRESH is its own. `refreshPigMetrics` runs after every pig
// entry autosave, and it has always refreshed the metrics and nothing else.
// Folding the RPC into the detail payload would make one weight edit re-read
// the animal directory and the whole species weigh-in history, and — worse —
// re-seed every row's draft in the table the operator is typing into. This is
// the same reason `pig.weighins.metrics` is split from `livestock.weighins.list`
// on the pig LIST surface.
//
// WHY THE RESIDUAL LIST IS EMPTY.
//
// This surface was never a former-eager consumer: `weighinsessions` is outside
// the frozen 53-view space in tests/fixtures/former-eager-dependencies.json,
// and tests/static/former_eager_evidence_static.test.js derives its evidence
// set as exactly empty. It consumes no App context and no boot array — App
// mounts the page as `<WeighInSessionPage />` with no props at all. So there is
// nothing left for `residualLegacy` to name, and `[]` here is a derived result
// rather than an omission.
//
// The page still WRITES: entry autosaves, session complete/reopen/delete, the
// pig send-to-trip and transfer-to-breeding flows, and the two ppp-feeders-v1 /
// ppp-breeders-v1 upserts those flows own. None of those are obligations. A
// setter is not a read, and the provenance derivation scores writes as
// non-consuming precisely so a localization is not forced to move a mutation it
// does not own in the same change.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.weighinsessions',
  obligations: Object.freeze([
    Object.freeze({id: 'weighin.session.record', owner: 'permanent'}),
    Object.freeze({id: 'weighin.session.detail', owner: 'permanent'}),
    Object.freeze({id: 'weighin.session.pigMetrics', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
